import type { UID } from '@strapi/strapi';
import axios from 'axios';
import type { APIResponse, APIResponseCollection } from 'common-types';
import qs from 'qs';
import { STRAPI_API_HOST } from 'services/api/config';

const axiosClient = axios.create({ baseURL: `${STRAPI_API_HOST}/api` });

export type TStrapiCollectionParams = {
  endpoint: string;
  page?: number;
  pageSize?: number;
  locale?: string | null;
  filters?: Record<string, unknown>;
  sort?: string | string[];
  populate?: Record<string, unknown> | string;
  fields?: string[];
  signal?: AbortSignal;
  publicationState?: string;
};

export const fetchStrapiCollection = async <T extends UID.ContentType>({
  endpoint,
  page = 1,
  pageSize = 25,
  locale,
  filters,
  sort,
  populate,
  fields,
  signal,
  publicationState,
}: TStrapiCollectionParams): Promise<APIResponseCollection<T>> => {
  const params: Record<string, unknown> = {
    pagination: { page, pageSize },
  };

  if (locale) {
    params.locale = locale;
  }

  if (publicationState) {
    params.publicationState = publicationState;
  }

  if (filters) {
    params.filters = filters;
  }

  if (sort) {
    params.sort = sort;
  }

  if (populate) {
    params.populate = populate;
  }

  if (fields) {
    params.fields = fields;
  }

  const queryString = qs.stringify(params, { addQueryPrefix: true });
  const response = await axiosClient.get<APIResponseCollection<T>>(`${endpoint}${queryString}`, { signal });

  return response.data;
};

export type TStrapiSingleParams = {
  endpoint: string;
  id: string | number;
  locale?: string | null;
  populate?: Record<string, unknown> | string;
  fields?: string[];
  signal?: AbortSignal;
};

export const fetchStrapiSingle = async <T extends UID.ContentType>({
  endpoint,
  id,
  locale,
  populate,
  fields,
  signal,
}: TStrapiSingleParams): Promise<APIResponse<T>> => {
  // Strapi v5: single entities are addressed by string `documentId`, not the numeric `id`
  // (GET /endpoint/:numericId returns 404). Resolve via a filtered collection query so both
  // legacy numeric ids and documentIds work.
  const filterKey = /^\d+$/.test(String(id)) ? 'id' : 'documentId';
  const params: Record<string, unknown> = {
    filters: { [filterKey]: { $eq: id } },
    pagination: { pageSize: 1 },
  };

  if (locale) {
    params.locale = locale;
  }

  if (populate) {
    params.populate = populate;
  }

  if (fields) {
    params.fields = fields;
  }

  const queryString = qs.stringify(params, { addQueryPrefix: true });
  const response = await axiosClient.get<APIResponseCollection<T>>(`${endpoint}${queryString}`, { signal });

  return { data: response.data.data[0] } as APIResponse<T>;
};

export type TStrapiSingleTypeParams = {
  endpoint: string;
  locale?: string | null;
  populate?: Record<string, unknown> | string;
  fields?: string[];
  signal?: AbortSignal;
  publicationState?: string;
};

export const fetchStrapiSingleType = async <T extends UID.ContentType>({
  endpoint,
  locale,
  populate,
  fields,
  signal,
  publicationState,
}: TStrapiSingleTypeParams): Promise<APIResponse<T>> => {
  const params: Record<string, unknown> = {};

  if (locale) {
    params.locale = locale;
  }

  if (publicationState) {
    params.publicationState = publicationState;
  }

  if (populate) {
    params.populate = populate;
  }

  if (fields) {
    params.fields = fields;
  }

  const queryString = qs.stringify(params, { addQueryPrefix: true });
  const response = await axiosClient.get<APIResponse<T>>(`${endpoint}${queryString}`, { signal });

  return response.data;
};
